import React, { useState } from "react";
import {
  Box, HStack, Text, Button, Input, Badge, Menu, MenuButton, MenuList, MenuItem, MenuOptionGroup, MenuItemOption,
} from "@chakra-ui/react";
import { FiClock, FiChevronDown, FiFilter, FiCheck } from "react-icons/fi";
import { DARK } from "./MemoryChart";
import {
  RANGE_OPTIONS, MAX_WINDOW_SEC, MIN_WINDOW_SEC, rangeLabel, formatMoment,
} from "./memoryChartData";
import { toLocalInputValue, fromLocalInputValue } from "./format";

const darkButton = {
  size: "sm", variant: "outline", color: DARK.text, borderColor: DARK.border, bg: DARK.panel,
  _hover: { bg: DARK.hover }, _active: { bg: DARK.hover },
};

// The range inputs only hold whole minutes, so "the last 14 days" can read a minute long.
const SLACK_MS = 2 * 60 * 1000;

/** Why a custom range can't be used, or null when it can. */
export function customRangeError(fromIso, toIso, now = Date.now()) {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return "Pick both a start and an end.";
  if (to <= from) return "The end has to be after the start.";
  if ((to - from) / 1000 < MIN_WINDOW_SEC) return "Pick at least 5 minutes.";
  if (to - from > MAX_WINDOW_SEC * 1000 + SLACK_MS) return "Pick at most 14 days.";
  if (from < now - MAX_WINDOW_SEC * 1000 - SLACK_MS) return "Samples are only kept for 14 days.";
  return null;
}

function CustomRange({ windowSec, at, onApply, onCancel }) {
  const end = at ? Date.parse(at) : Date.now();
  const [from, setFrom] = useState(toLocalInputValue(new Date(end - windowSec * 1000).toISOString()));
  const [to, setTo] = useState(toLocalInputValue(new Date(end).toISOString()));
  const fromIso = fromLocalInputValue(from);
  const toIso = fromLocalInputValue(to);
  const error = customRangeError(fromIso, toIso);
  const inputStyle = { size: "sm", w: "205px", type: "datetime-local", color: DARK.text, borderColor: DARK.border,
                       bg: DARK.menu, sx: { colorScheme: "dark" } };
  return (
    <HStack spacing={2} flexWrap="wrap" mt={2} data-testid="custom-range">
      <Text fontSize="sm" color={DARK.muted}>From</Text>
      <Input {...inputStyle} aria-label="range start" value={from} onChange={(e) => setFrom(e.target.value)} />
      <Text fontSize="sm" color={DARK.muted}>to</Text>
      <Input {...inputStyle} aria-label="range end" value={to} onChange={(e) => setTo(e.target.value)} />
      <Button size="sm" colorScheme="purple" isDisabled={!!error} onClick={() => onApply(fromIso, toIso)}>Apply</Button>
      <Button size="sm" variant="ghost" color={DARK.muted} _hover={{ bg: DARK.hover }} onClick={onCancel}>Cancel</Button>
      {error && <Text fontSize="xs" color="#fca5a5">{error}</Text>}
    </HStack>
  );
}

/**
 * Render's time-range menu. A preset follows the present (live); Custom fixes both ends.
 * While the dashboard is pinned to a past moment, the label says until when.
 */
export function RangeMenu({ windowSec, at, onPreset, onCustom }) {
  const [customOpen, setCustomOpen] = useState(false);
  const label = at ? `${formatMoment(Date.parse(at) - windowSec * 1000)} – ${formatMoment(Date.parse(at))}` : rangeLabel(windowSec);
  return (
    <Box>
      <Menu placement="bottom-start" isLazy>
        <MenuButton as={Button} {...darkButton} leftIcon={<FiClock />} rightIcon={<FiChevronDown />} data-testid="range-menu">
          {label}
        </MenuButton>
        <MenuList bg={DARK.menu} borderColor={DARK.border} color={DARK.text} zIndex={20} py={1}>
          {RANGE_OPTIONS.map((option) => {
            const selected = !at && option.sec === windowSec;
            return (
              <MenuItem key={option.label} icon={<FiClock />} isDisabled={option.disabled} title={option.hint}
                        bg={selected ? DARK.selected : DARK.menu} borderLeft="2px solid"
                        borderLeftColor={selected ? DARK.accent : "transparent"} _hover={{ bg: selected ? DARK.selected : DARK.hover }}
                        onClick={() => { setCustomOpen(false); onPreset(option.sec); }}>
                <HStack justify="space-between" w="full">
                  <Text>{option.label}</Text>
                  {selected && <FiCheck />}
                </HStack>
              </MenuItem>
            );
          })}
          <MenuItem icon={<FiClock />} bg={at ? DARK.selected : DARK.menu} borderLeft="2px solid"
                    borderLeftColor={at ? DARK.accent : "transparent"} _hover={{ bg: DARK.hover }}
                    onClick={() => setCustomOpen(true)}>
            Custom
          </MenuItem>
        </MenuList>
      </Menu>
      {customOpen && (
        <CustomRange windowSec={windowSec} at={at} onCancel={() => setCustomOpen(false)}
                     onApply={(fromIso, toIso) => { setCustomOpen(false); onCustom(fromIso, toIso); }} />
      )}
    </Box>
  );
}

export const EVENT_TYPES = [
  { value: "restarts", label: "Restarts and deploys" },
  { value: "jumps", label: "Memory jumps" },
];

/** Render's "Filter events": which kinds of markers the chart shows. */
export function EventsFilter({ value, onChange }) {
  return (
    <Menu closeOnSelect={false} placement="bottom-start" isLazy>
      <MenuButton as={Button} {...darkButton} leftIcon={<FiFilter />} rightIcon={<FiChevronDown />} data-testid="events-filter">
        Filter events <Badge ml={2} bg={DARK.hover} color={DARK.text}>{value.length}</Badge>
      </MenuButton>
      <MenuList bg={DARK.menu} borderColor={DARK.border} color={DARK.text} zIndex={20}>
        <MenuOptionGroup type="checkbox" value={value} onChange={(next) => onChange(Array.isArray(next) ? next : [next])}>
          {EVENT_TYPES.map((type) => (
            <MenuItemOption key={type.value} value={type.value} bg={DARK.menu} _hover={{ bg: DARK.hover }}>
              {type.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}
